import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Menu, Leaf } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
  SheetClose,
} from "@/components/ui/sheet";
import { scrollToId } from "@/lib/scroll";
import { cn } from "@/lib/utils";

const LINKS = [
  { id: "about", label: "About" },
  { id: "products", label: "Product" },
  { id: "menu", label: "Menu" },
  { id: "science", label: "Science" },
  { id: "join", label: "Opportunity" },
  { id: "testimonials", label: "Stories" },
  { id: "faq", label: "FAQ" },
  { id: "contact", label: "Contact" },
];

export const Navbar = ({ config, bannerOffset = false }) => {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const brand = config?.brandName || "Ecentra";

  return (
    <header
      style={{ top: bannerOffset ? 40 : 0 }}
      className={cn(
        "fixed inset-x-0 z-50 transition-[background-color,box-shadow,top] duration-300",
        scrolled
          ? "border-b border-border bg-background/90 shadow-sm backdrop-blur supports-[backdrop-filter]:bg-background/75"
          : "bg-transparent"
      )}
      data-testid="main-navbar"
    >
      <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-4 sm:px-6 lg:px-8">
        <button
          onClick={() => scrollToId("top")}
          className="flex items-center gap-2"
          data-testid="navbar-logo"
          aria-label={`${brand} home`}
        >
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary text-primary-foreground">
            <Leaf className="h-5 w-5" />
          </span>
          <span className="font-display text-xl font-semibold tracking-tight text-foreground">
            {brand}
          </span>
        </button>

        <nav className="hidden items-center gap-7 lg:flex">
          {LINKS.map((l) => (
            <button
              key={l.id}
              onClick={() => scrollToId(l.id)}
              className="text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
              data-testid={`nav-link-${l.id}`}
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="hidden items-center gap-2 lg:flex">
          <Button
            variant="ghost"
            onClick={() => scrollToId("products")}
            data-testid="nav-shop-button"
          >
            Shop
          </Button>
          <Button
            className="bg-accent text-accent-foreground hover:bg-accent/90"
            onClick={() => scrollToId("join")}
            data-testid="nav-join-button"
          >
            Join My Team
          </Button>
        </div>

        {/* Mobile */}
        <div className="lg:hidden">
          <Sheet>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" aria-label="Buksan ang menu" data-testid="mobile-menu-button">
                <Menu className="h-6 w-6" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[86%] max-w-sm">
              <div className="mt-4 flex items-center gap-2">
                <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary text-primary-foreground">
                  <Leaf className="h-5 w-5" />
                </span>
                <span className="font-display text-xl font-semibold">{brand}</span>
              </div>
              <nav className="mt-8 flex flex-col gap-1">
                {LINKS.map((l) => (
                  <SheetClose asChild key={l.id}>
                    <button
                      onClick={() => scrollToId(l.id)}
                      className="rounded-lg px-3 py-3 text-left text-base font-medium text-foreground transition-colors hover:bg-muted"
                      data-testid={`mobile-nav-link-${l.id}`}
                    >
                      {l.label}
                    </button>
                  </SheetClose>
                ))}
              </nav>
              <div className="mt-8 flex flex-col gap-3">
                <SheetClose asChild>
                  <Button variant="outline" onClick={() => scrollToId("products")}>
                    Mag-shop
                  </Button>
                </SheetClose>
                <SheetClose asChild>
                  <Button
                    className="bg-accent text-accent-foreground hover:bg-accent/90"
                    onClick={() => scrollToId("join")}
                  >
                    Join My Team
                  </Button>
                </SheetClose>
                <Link
                  to="/admin"
                  className="mt-2 text-center text-xs text-muted-foreground underline-offset-4 hover:underline"
                >
                  Distributor login
                </Link>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};
